import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Award, Calendar, Trophy, Users } from "lucide-react";

const FoundersSocialProofSection = () => {
  const achievements = [
    {
      category: "Awards",
      icon: Trophy,
      items: [
        { title: "Cannes Lions Bronze", year: "2023", description: "Best Cinematography - Commercial Category" },
        { title: "Sundance Film Festival", year: "2023", description: "Official Selection - Short Film" },
        { title: "Vimeo Staff Pick", year: "2023", description: "Featured for 'Echoes of Tomorrow'" },
        { title: "AdAge Creative Award", year: "2022", description: "Excellence in Visual Storytelling" }
      ]
    },
    {
      category: "Publications",
      icon: Users,
      items: [
        { title: "American Cinematographer", year: "2023", description: "Featured Article: 'Modern Music Video Techniques'" },
        { title: "Creative Review", year: "2023", description: "Spotlight: Rising Cinematographers" },
        { title: "Film Quarterly", year: "2022", description: "Interview: 'The Art of Visual Narrative'" },
        { title: "Campaign Magazine", year: "2022", description: "Case Study: Heritage Crafts Campaign" }
      ]
    },
    {
      category: "Recognition",
      icon: Award,
      items: [
        { title: "LA Film Society", year: "2023", description: "Emerging Cinematographer of the Year" },
        { title: "Music Video Awards", year: "2023", description: "Best Cinematography Nomination" },
        { title: "Directors Guild", year: "2022", description: "Honorable Mention - Visual Excellence" },
        { title: "Industry Choice Awards", year: "2022", description: "Critic's Pick - Best Newcomer" }
      ]
    },
    {
      category: "Speaking",
      icon: Calendar,
      items: [
        { title: "NAB Show 2023", year: "2023", description: "Panel: 'Future of Cinematography'" },
        { title: "SXSW Film Conference", year: "2023", description: "Workshop: 'Music Video Production'" },
        { title: "FilmIndependent Forum", year: "2022", description: "Masterclass: 'Visual Storytelling'" },
        { title: "USC Film School", year: "2022", description: "Guest Lecture: 'Career in Cinematography'" }
      ]
    }
  ];

  const clientLogos = [
    "Netflix", "Sony Music", "Universal", "Warner Bros", "Adobe", "Nike", 
    "Apple", "Spotify", "Amazon", "Disney", "HBO", "Paramount"
  ];

  return (
    <section id="social-proof" className="py-24 bg-muted/30">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="text-center mb-16 fade-in-up">
          <h2 className="heading-text mb-6 text-foreground">
            Recognition & Achievements
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Industry recognition and professional achievements that demonstrate 
            our commitment to excellence and innovation in cinematography.
          </p>
        </div>

        {/* Achievement Categories */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {achievements.map((category, categoryIndex) => {
            const IconComponent = category.icon;
            return (
              <Card 
                key={category.category}
                className="shadow-medium hover:shadow-dramatic transition-all duration-500 fade-in-up"
                style={{ animationDelay: `${categoryIndex * 0.1}s` }}
              >
                <CardContent className="p-6">
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 accent-gradient rounded-full flex items-center justify-center mr-3">
                      <IconComponent size={24} className="text-primary-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground">
                      {category.category}
                    </h3>
                  </div>
                  
                  <div className="space-y-4">
                    {category.items.map((item, itemIndex) => (
                      <div key={itemIndex} className="border-l-2 border-primary/20 pl-4 pb-4 last:pb-0">
                        <div className="flex items-start justify-between mb-1">
                          <h4 className="font-medium text-foreground text-sm">
                            {item.title}
                          </h4>
                          <Badge variant="outline" className="text-xs ml-2">
                            {item.year}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Client Logos */}
        <div className="fade-in-up">
          <h3 className="text-center text-xl font-semibold text-foreground mb-8">
            Trusted by Industry Leaders
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 items-center opacity-60">
            {clientLogos.map((client, index) => (
              <div 
                key={client}
                className="text-center text-muted-foreground font-medium text-sm hover:text-foreground transition-colors duration-300 cursor-pointer"
              >
                {client}
              </div>
            ))}
          </div>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-20 fade-in-up">
          <div className="text-center">
            <div className="text-4xl font-bold text-primary mb-2">15+</div>
            <p className="text-sm text-muted-foreground">Industry Awards</p>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-primary mb-2">8</div>
            <p className="text-sm text-muted-foreground">Featured Publications</p>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-primary mb-2">12</div>
            <p className="text-sm text-muted-foreground">Major Brands</p>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-primary mb-2">4</div>
            <p className="text-sm text-muted-foreground">Speaking Engagements</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FoundersSocialProofSection;